import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import TripPointPicker from "./TripPointPicker";

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
  fontSize: 13.5, color: "#111827", outline: "none",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };

export default function NewTripModal({ vehicles, onClose, onSubmit }) {
  const [vehicleId, setVehicleId]   = useState(vehicles[0]?.id || "");
  const [label,     setLabel]       = useState("");
  const [mode,      setMode]        = useState("source");
  const [source,      setSource]      = useState(null);
  const [destination, setDestination] = useState(null);
  const [sourceLabel,      setSourceLabel]      = useState("");
  const [destinationLabel, setDestinationLabel] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [radiusKm,     setRadiusKm]     = useState(20);
  const [saving, setSaving] = useState(false);

  function handlePick(pickMode, point) {
    if (pickMode === "source") {
      setSource(point);
      if (!destination) setMode("destination");
    } else {
      setDestination(point);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!vehicleId) return toast.error("Select a vehicle");
    if (!source) return toast.error("Set the source point on the map");
    if (!destination) return toast.error("Set the destination point on the map");

    setSaving(true);
    try {
      await onSubmit({
        vehicleId,
        label: label.trim() || null,
        source: { ...source, label: sourceLabel.trim() || null },
        destination: { ...destination, label: destinationLabel.trim() || null },
        radiusKm,
      });
      toast.success("Trip created");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create trip");
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 560,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
            maxHeight: "92vh", display: "flex", flexDirection: "column",
          }}
        >
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>New Trip</div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Vehicle *</label>
                <select style={inputStyle} value={vehicleId} onChange={e => setVehicleId(e.target.value)}>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.nickname || v.plateNumber}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Trip Label</label>
                <input style={inputStyle} value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Delhi → Mumbai" />
              </div>
            </div>

            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <button type="button" onClick={() => setMode("source")}
                  style={{
                    flex: 1, height: 32, borderRadius: 7, cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                    border: mode === "source" ? "1.5px solid #16A34A" : "1px solid #E5E7EB",
                    background: mode === "source" ? "#F0FDF4" : "#fff",
                    color: mode === "source" ? "#16A34A" : "#6B7280",
                  }}
                >● Source {source ? "✓" : ""}</button>
                <button type="button" onClick={() => setMode("destination")}
                  style={{
                    flex: 1, height: 32, borderRadius: 7, cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                    border: mode === "destination" ? "1.5px solid #DC2626" : "1px solid #E5E7EB",
                    background: mode === "destination" ? "#FEF2F2" : "#fff",
                    color: mode === "destination" ? "#DC2626" : "#6B7280",
                  }}
                >● Destination {destination ? "✓" : ""}</button>
              </div>
              <TripPointPicker source={source} destination={destination} mode={mode} onPick={handlePick} />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Source Label</label>
                <input style={inputStyle} value={sourceLabel} onChange={e => setSourceLabel(e.target.value)} placeholder="e.g. Delhi Warehouse" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Destination Label</label>
                <input style={inputStyle} value={destinationLabel} onChange={e => setDestinationLabel(e.target.value)} placeholder="e.g. Mumbai Depot" />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 4, background: "none", border: "none",
                cursor: "pointer", color: "#6B7280", fontSize: 12.5, fontWeight: 600, padding: 0, alignSelf: "flex-start",
              }}
            >
              {showAdvanced ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Advanced
            </button>
            {showAdvanced && (
              <div>
                <label style={labelStyle}>Detection Radius (km)</label>
                <input
                  type="number" min={1} style={inputStyle}
                  value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value) || 20)}
                />
                <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 4 }}>
                  How close a toll crossing must be to the source/destination point to count as "reached." Default 20km.
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              style={{
                marginTop: 6, width: "100%", height: 42, borderRadius: 10, border: "none",
                background: saving ? "#9CA3AF" : "#4F46E5",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {saving ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Creating…</> : "Create Trip"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
